'use client';

import Link from 'next/link';
import { useActsLanguage } from '../../../utils/useActsLanguage';
import { ACTS_COPY, CHANGES_PAGE_COPY } from '../../../utils/actsTranslations';
import type { ActChangeSet } from '@/api/services/actChanges.service';
import ChangeDiffView from './ChangeDiffView';
import PublicHeaderBrand from '../../../../components/PublicHeaderBrand';

/**
 * One change-set (all article changes detected on a given date) for an act:
 * /ustawa/{slug}/zmiany/{date}. Renders each article's before/after word-diff.
 * Client component only for the per-article expander; Next server-renders it so
 * both old and new text are in the initial HTML.
 */
export default function ZmianyDiffPageClient({
  slug,
  actTitle,
  date,
  changeSet,
}: {
  slug: string;
  actTitle: string;
  date: string;
  changeSet: ActChangeSet | null;
}) {
  const language = useActsLanguage();
  const copy = CHANGES_PAGE_COPY[language];
  const acts = ACTS_COPY[language];

  const formatDate = (iso: string): string => {
    try {
      return new Date(iso + 'T00:00:00Z').toLocaleDateString(
        language === 'vi' ? 'vi-VN' : language === 'uk' ? 'uk-UA' : 'pl-PL',
        { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' },
      );
    } catch {
      return iso;
    }
  };

  return (
    <main className="min-h-screen bg-[var(--surface-primary)]">
      <header className="bg-[var(--navbar-background)] text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <PublicHeaderBrand />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-[var(--text-secondary)] mb-6 flex flex-wrap gap-2">
          <Link href="/" className="hover:text-[var(--interactive-primary)]">
            {acts.common.home}
          </Link>
          <span>/</span>
          <Link href="/ustawa" className="hover:text-[var(--interactive-primary)]">
            {acts.common.acts}
          </Link>
          <span>/</span>
          <Link href={`/ustawa/${slug}`} className="hover:text-[var(--interactive-primary)]">
            {actTitle}
          </Link>
          <span>/</span>
          <Link
            href={`/ustawa/${slug}/zmiany`}
            className="hover:text-[var(--interactive-primary)]"
          >
            {copy.timelineHeading}
          </Link>
          <span>/</span>
          <span className="text-[var(--text-primary)]">{formatDate(date)}</span>
        </nav>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-2">
            {copy.timelineHeading} - {formatDate(date)}
          </h1>
          <p className="text-[var(--text-secondary)]">{actTitle}</p>
          <p className="text-sm text-[var(--text-secondary)] mt-2">
            {copy.detectionDateNote}
          </p>
        </div>

        {!changeSet || changeSet.events.length === 0 ? (
          <section
            className="rounded-lg p-8 text-center"
            style={{ backgroundColor: 'var(--surface-elevated)' }}
          >
            <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
              {copy.dateNotFoundTitle}
            </h2>
            <p className="text-[var(--text-secondary)]">{copy.dateNotFoundText}</p>
          </section>
        ) : (
          <>
            <div className="mb-4 flex gap-4 text-sm text-[var(--text-secondary)]">
              <span>
                <span
                  className="inline-block w-3 h-3 rounded-sm mr-1 align-middle"
                  style={{
                    backgroundColor:
                      'color-mix(in srgb, var(--state-success) 40%, transparent)',
                  }}
                />
                {copy.afterLabel}
              </span>
              <span>
                <span
                  className="inline-block w-3 h-0 border-t-2 mr-1 align-middle"
                  style={{ borderColor: 'var(--state-error)' }}
                />
                {copy.beforeLabel}
              </span>
            </div>
            {changeSet.events.map((ev, i) => (
              <ChangeDiffView key={`${ev.article_label}-${i}`} event={ev} copy={copy} />
            ))}
          </>
        )}

        <Link
          href={`/ustawa/${slug}/zmiany`}
          className="inline-block mt-6 text-sm font-medium hover:underline"
          style={{ color: 'var(--interactive-primary)' }}
        >
          ← {copy.backToHistory}
        </Link>
      </div>
    </main>
  );
}
